import { ArrowRightIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Header } from "../../components/Header";
import { getImageUrl } from "../../lib/imageUrls";

interface Project {
  name: string;
  image: string;
  link?: string;
}

export const HomeWheel = (): JSX.Element => {
  const projects: Project[] = [
    { name: "Volvo Cars", image: getImageUrl("/3d-homepage/img1.png") },
    { name: "Formula1.com", image: getImageUrl("/3d-homepage/img2.png"), link: "/formula1" },
    { name: "H&M", image: getImageUrl("/3d-homepage/img3.png") },
    { name: "Biesdorf", image: getImageUrl("/3d-homepage/img4.png") },
    { name: "Nissan", image: getImageUrl("/3d-homepage/img5.png") },
    { name: "Volvo Rider", image: getImageUrl("/3d-homepage/img6.png") },
    { name: "Lexus", image: getImageUrl("/3d-homepage/img7.png") },
    { name: "Volvo Penta", image: getImageUrl("/3d-homepage/img8.png") },
    { name: "XC40 Recharge", image: getImageUrl("/3d-homepage/img9.png") },
    { name: "Kasthall", image: getImageUrl("/3d-homepage/img10.png") },
    { name: "Volvo Trucks", image: getImageUrl("/3d-homepage/img11.png") },
    { name: "Dieter Rams", image: getImageUrl("/3d-homepage/img12.png") },
    { name: "Photography", image: getImageUrl("/3d-homepage/img13.png") },
  ];

  const [activeProject, setActiveProject] = useState<number>(0);
  const projectRefs = useRef<(HTMLLIElement | null)[]>([]);
  const projectListRef = useRef<HTMLUListElement>(null);
  const wheelRef = useRef<HTMLDivElement>(null);
  const ticking = useRef(false);

  useEffect(() => {
    const handleScroll = () => {
      if (!ticking.current) {
        window.requestAnimationFrame(() => {
          const windowHeight = window.innerHeight;
          const middleY = windowHeight / 2;

          if (projectListRef.current) {
            let closestIndex = 0;
            let closestDistance = Infinity;

            projectRefs.current.forEach((ref, index) => {
              if (ref) {
                const rect = ref.getBoundingClientRect();
                const distance = Math.abs((rect.top + rect.bottom) / 2 - middleY);
                
                if (distance < closestDistance) {
                  closestDistance = distance;
                  closestIndex = index;
                }
              }
            });

            setActiveProject(closestIndex);

            // Rotate wheel
            if (wheelRef.current) {
              const rotationDegree = (360 / projects.length) * closestIndex;
              wheelRef.current.style.transform = `rotate(${rotationDegree}deg)`;
            }
          }

          ticking.current = false;
        });

        ticking.current = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [projects.length]);

  return (
    <main className="bg-white flex flex-row justify-center w-full min-h-screen">
      <div className="bg-white relative w-full max-w-[1442px] min-h-screen pb-[600px] lg:pb-[800px] px-4 lg:px-8 mx-auto">
        <Header />
        
        {/* Bio section */}
        <section className="pt-[180px] lg:pt-[197px] lg:pl-[600px] lg:pr-8 relative z-40">
          <p className="w-[90%] lg:w-full max-w-[522px] [font-family:'Millionaire-Roman',Helvetica] font-normal text-black text-base lg:text-xl leading-6 lg:leading-10">
            Designer and Creative Leader at Rapid Images, leading innovation in digital design and brand experiences. Two decades of expertise in creative direction, working with global brands to deliver transformative visual solutions and strategic design leadership.
          </p>
        </section>

        {/* Wheel Container */}
        <div className="fixed top-1/2 right-0 w-[80px] h-[80px] pr-4 -translate-y-1/2 md:w-[80px] md:h-[80px] md:pr-0 lg:left-0 lg:top-0 lg:w-[50%] lg:h-screen lg:translate-y-0 flex items-center justify-center overflow-hidden">
          <div className="relative w-[600px] h-[600px]">
            {/* Center point indicator */}
            <div className="absolute left-1/2 top-1/2 w-2 h-2 bg-black rounded-full transform -translate-x-1/2 -translate-y-1/2 z-50" />
            
            {/* Wheel */}
            <div 
              ref={wheelRef}
              className="absolute w-full h-full transition-transform duration-700 ease-out"
              style={{ transformOrigin: 'center center' }}
            >
              {projects.map((project, index) => {
                const angle = (360 / projects.length) * index;
                const radius = 250; // Adjusted radius for smaller wheel
                
                return (
                  <div
                    key={index}
                    className="absolute origin-bottom transition-opacity duration-300"
                    style={{
                      width: '160px',
                      height: '120px',
                      left: '50%',
                      top: '50%',
                      transform: `
                        rotate(${angle}deg)
                        translateY(-${radius}px)
                        translateX(-50%)
                        rotate(${-angle}deg)
                      `,
                      transformOrigin: 'bottom center',
                      opacity: index === activeProject ? 1 : 0.2,
                    }}
                  >
                    <img
                      src={project.image}
                      alt={project.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Arrow icon - Desktop only */}
        <div className="hidden lg:block fixed top-1/2 left-[45%] z-40">
          <ArrowRightIcon className="w-6 h-6" />
        </div>

        {/* Projects list */}
        <section className="relative mt-[60px] lg:mt-0 lg:pt-[280px] w-[90%] lg:w-auto lg:pl-[600px] z-40">
          <ul 
            ref={projectListRef} 
            className="[font-family:'Millionaire-Roman',Helvetica] font-normal text-black text-3xl lg:text-6xl tracking-[-2px] lg:tracking-[-3px] leading-[48px] lg:leading-[84px]"
          >
            {projects.map((project, index) => (
              <li
                key={index}
                ref={el => projectRefs.current[index] = el}
                className={`hover:opacity-70 transition-all cursor-pointer will-change-contents ${
                  index === activeProject ? 'text-black' : 'text-black opacity-20'
                }`}
              >
                {project.link ? (
                  <Link to={project.link}>{project.name}</Link>
                ) : (
                  project.name
                )}
              </li>
            ))}
          </ul>
        </section>

        {/* Footer */}
        <footer className="absolute bottom-[100px] lg:left-[640px] z-40">
          <p className="[font-family:'ItemsTextTrial-Light',Helvetica] font-light text-black text-sm lg:text-base">
            © 2025 Bes Bujupi
          </p>
        </footer>
      </div>
    </main>
  );
};